import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class CookiesService {
  public getUserId(pwaId: string): string {
    return this.get('visitor_' + pwaId);
  }

  get(name: string): string {
    const value = document.cookie.replace(new RegExp(`(?:(?:^|.*;\\s*)${name}\\s*=\\s*([^;]*).*$)|^.*$`), '$1');
    return value || '';
  }

  set(name: string, value: string, options: CookieOptions = {}): void {
    let cookieString = `${name}=${value}`;

    if (options.expires) {
      if (typeof options.expires === 'number') {
        const date = new Date();
        date.setTime(date.getTime() + options.expires * 24 * 60 * 60 * 1000);
        cookieString += `; expires=${date.toUTCString()}`;
      } else {
        cookieString += `; expires=${options.expires.toUTCString()}`;
      }
    }

    if (options.path) {
      cookieString += `; path=${options.path}`;
    }

    if (options.domain) {
      cookieString += `; domain=${options.domain}`;
    }

    if (options.secure) {
      cookieString += '; secure';
    }

    if (options.sameSite) {
      cookieString += `; samesite=${options.sameSite}`;
    }

    if (options.httpOnly) {
      cookieString += '; httponly';
    }

    document.cookie = cookieString;
  }

  /**
   * Delete a cookie
   */
  delete(name: string, path?: string, domain?: string): void {
    this.set(name, '', {
      expires: new Date(0),
      path,
      domain,
    });
  }

  /**
   * Check if a cookie exists
   */
  exists(name: string): boolean {
    return this.get(name) !== '';
  }

  /**
   * Get all cookies as an object
   */
  getAll(): { [key: string]: string } {
    const cookies: { [key: string]: string } = {};

    document.cookie.split(';').forEach((cookie) => {
      const [name, value] = cookie.trim().split('=');
      if (name) {
        cookies[name] = value || '';
      }
    });

    return cookies;
  }

  /**
   * Get specific Facebook tracking cookies
   */
  getFacebookPixelCookies(): FacebookCookies {
    return {
      fbp: this.get('_fbp'),
      fbc: this.get('_fbc'),
      fbclid: this.get('fbclid'),
    };
  }

  /**
   * Get visitor cookie specifically
   */
  getVisitor(): string {
    return this.get('visitor');
  }

  /**
   * Get common tracking cookies
   */
  getTrackingCookies(): TrackingCookies {
    return {
      visitor: this.get('visitor'),
      fbp: this.get('_fbp'),
      fbc: this.get('_fbc'),
      fbclid: this.get('fbclid'),
      gaid: this.get('_ga'),
      gtag: this.get('_gtag'),
    };
  }
}

// Interfaces
export interface CookieOptions {
  expires?: number | Date; // days or Date object
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
  httpOnly?: boolean;
}

export interface FacebookCookies {
  fbp: string;
  fbc: string;
  fbclid: string;
}

export interface TrackingCookies {
  visitor: string;
  fbp: string;
  fbc: string;
  fbclid: string;
  gaid: string;
  gtag: string;
}
